import React, { useState } from 'react'
import Header from './header'
import { useLocation } from 'react-router-dom'
import { ConvertorMiladi } from 'utilities/helper/digitToMonth'

interface IUserInfoProps {
    data: FinancialHistoryUser
}

// Android @drawable/info tinted @color/colorPrimary (#74B743):
// a filled green circle with a white 'i' glyph cut out.
function InfoMark() {
    return (
        <svg
            className=" shrink-0 mt-[7px]"
            width="16"
            height="16"
            viewBox="0 0 50 50"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle cx="25" cy="25" r="23" fill="#74B743" />
            <path
                d="M25,11A3,3 0,0 0,22 14A3,3 0,0 0,25 17A3,3 0,0 0,28 14A3,3 0,0 0,25 11zM21,21L21,23L22,23L23,23L23,36L22,36L21,36L21,38L22,38L23,38L27,38L28,38L29,38L29,36L28,36L27,36L27,21L26,21L22,21L21,21z"
                fill="#fff"
            />
        </svg>
    )
}

// Android rows: label (start), value TextView with marginEnd=20dp,
// info icon gravity=end + marginTop=7dp (sits at far row end, offset down).
function InfoRow({
    label,
    children,
}: {
    label: string
    children: React.ReactNode
}) {
    return (
        <div className=" flex items-start justify-between gap-5 mt-0.5">
            <span className=" text-sm text-text-secondary">{label}</span>
            <span className=" flex items-start">
                <span className=" me-5">{children}</span>
                <InfoMark />
            </span>
        </div>
    )
}

export default function UserInfo({ data }: IUserInfoProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const { state } = useLocation()
    return (
        <div className=" bg-white px-2 pt-2">
            {/* member name */}
            <p className=" text-center text-base font-bold text-black mt-2">
                {data.member?.first_name} {data.member?.last_name}
            </p>

            {/* info box */}
            <div className=" mt-2 mx-2 bg-[#EFEFEF] border border-[#D4D4D4] rounded-t-[4px] p-2 flex flex-col items-stretch">
                <Header title="اطلاعات کلی" />

                <InfoRow label="سرمایه شخصی">
                    <span className=" text-sm font-bold text-black">
                        {data.capital && data.capital.toLocaleString()} تومان
                    </span>
                </InfoRow>

                <InfoRow label="مجموع بدهی">
                    <span className=" text-sm font-bold text-black">
                        {data.debit_amount &&
                            data.debit_amount.toLocaleString()}{' '}
                        تومان
                    </span>
                </InfoRow>

                <InfoRow label="تاریخ عضویت">
                    <span className=" text-sm font-bold text-black">
                        {!!data.join_datetime &&
                            ConvertorMiladi(data.join_datetime)}
                    </span>
                </InfoRow>

                {data && data.guarantor && (
                    <InfoRow label="ضامن عضو">
                        <span className=" text-sm font-bold text-black">
                            {data.guarantor.member.first_name}{' '}
                            {data.guarantor.member.last_name}
                        </span>
                    </InfoRow>
                )}

                {data && data.recommender && (
                    <InfoRow label="معرف عضو">
                        <span className=" text-sm font-bold text-black">
                            {data.recommender.member.first_name}{' '}
                            {data.recommender.member.last_name}
                        </span>
                    </InfoRow>
                )}

                {isOpen && (
                    <>
                        <Header title="تعهدات" />
                        <InfoRow label="حق‌عضویت این دوره">
                            <span className=" text-sm font-bold text-black text-left">
                                {data.number_of_shares &&
                                    (
                                        Number(state?.shareValueAmount) *
                                        Number(data.number_of_shares)
                                    ).toLocaleString()}{' '}
                                تومان معادل{' '}
                                {data.number_of_shares &&
                                    Number(
                                        data.number_of_shares
                                    ).toLocaleString()}{' '}
                                سهم
                            </span>
                        </InfoRow>
                        <InfoRow label="اقساط این دوره">
                            <span className=" text-sm font-bold text-black">
                                {data.normal_unpaid_installments_amount &&
                                    data.normal_unpaid_installments_amount.toLocaleString()}{' '}
                                تومان
                            </span>
                        </InfoRow>
                        <InfoRow label="تعهدات پرداخت نشده">
                            <span className=" text-sm font-bold text-error-3">
                                {data.interval_commitments &&
                                    data.interval_commitments.toLocaleString()}{' '}
                                تومان
                            </span>
                        </InfoRow>

                        <Header title="وام‌ها" />
                        <InfoRow label="تعداد وام‌های دریافتی">
                            <span className=" text-sm font-bold text-black">
                                {data.num_loans &&
                                    data.num_loans.toLocaleString()}{' '}
                                وام
                            </span>
                        </InfoRow>
                        <InfoRow label="محموع وام‌های دریافتی">
                            <span className=" text-sm font-bold text-black">
                                {data.loans_amount &&
                                    data.loans_amount.toLocaleString()}{' '}
                                تومان
                            </span>
                        </InfoRow>
                        <InfoRow label="تعداد اقساط پرداخت نشده">
                            <span className=" text-sm font-bold text-black">
                                {data.number_of_installments &&
                                    Number(data.number_of_installments) -
                                        Number(
                                            data.number_of_paid_installments
                                        ) &&
                                    (
                                        Number(data.number_of_installments) -
                                        Number(
                                            data.number_of_paid_installments
                                        )
                                    ).toLocaleString()}{' '}
                                قسط
                            </span>
                        </InfoRow>
                        <InfoRow label="تعداد اقساط معوق">
                            <span className=" text-sm font-bold text-black">
                                {data.number_of_delayed_installments &&
                                    data.number_of_delayed_installments.toLocaleString()}{' '}
                                قسط
                            </span>
                        </InfoRow>

                        <Header title="اطلاعات دفتری" />
                        <InfoRow label="مجموع واریزها">
                            <span className=" text-sm font-bold text-black">
                                {data.asset_value &&
                                    data.asset_value.toLocaleString()}{' '}
                                تومان
                            </span>
                        </InfoRow>
                        <InfoRow label="مجموع برداشت‌ها">
                            <span className=" text-sm font-bold text-black">
                                {data.debt_value &&
                                    data.debt_value.toLocaleString()}{' '}
                                تومان
                            </span>
                        </InfoRow>
                    </>
                )}
            </div>

            {/* more / less info toggle button attached to bottom of box */}
            <div className=" flex justify-center -mt-px">
                <div
                    className=" flex items-center justify-center gap-2 bg-[#EFEFEF] border border-[#D4D4D4] rounded-b-[4px] px-2 py-1 cursor-pointer"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {/* Android @drawable/ic_up = ic_arrow_left_black rotated 90deg,
                        tinted @color/hamyan_blue_500 (#2196F3). Flips on toggle. */}
                    <svg
                        className=" w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                            transform: isOpen ? 'none' : 'rotate(180deg)',
                        }}
                    >
                        <path
                            d="M6 14L12 8L18 14"
                            stroke="#2196F3"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span className=" text-sm font-bold text-[#2196F3]">
                        {isOpen ? 'اطلاعات کمتر' : 'اطلاعات بیشتر'}
                    </span>
                </div>
            </div>
        </div>
    )
}
